import {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  Firestore,
  QueryDocumentSnapshot,
  collection,
  doc,
  getDoc,
  getDocs,
  initializeFirestore,
  limit,
  query,
  where,
} from 'firebase/firestore'

import { getApp } from 'firebase/app'

export enum IEventName {
  CREATE_CONTACT = 'CREATE_CONTACT',
  IMPORT_CONTACT = 'IMPORT_CONTACT',
  SETUP_FORM = 'SETUP_FORM',
  CREATE_FUNNEL = 'CREATE_FUNNEL',
  CREATE_FUNNEL_STEP = 'CREATE_FUNNEL_STEP',
  PUBLISH_FUNNEL = 'PUBLISH_FUNNEL',
  SETUP_DOMAIN = 'SETUP_DOMAIN',
  SETUP_EMAIL_SERVICE = 'SETUP_EMAIL_SERVICE',
  SETUP_PHONE_SERVICE = 'SETUP_PHONE_SERVICE',
  CREATE_EMAIL_TEMPLATE = 'CREATE_EMAIL_TEMPLATE',
  CREATE_EMAIL_CAMPAIGN = 'CREATE_EMAIL_CAMPAIGN',
  SEND_EMAIL_CAMPAIGN = 'SEND_EMAIL_CAMPAIGN',
  CREATE_WORKFLOW = 'CREATE_WORKFLOW',
  PUBLISH_WORKFLOW = 'PUBLISH_WORKFLOW',
  SETUP_CALENDAR = 'SETUP_CALENDAR',
  CREATE_PIPELINE = 'CREATE_PIPELINE',
  CREATE_OPPORTUNITY = 'CREATE_OPPORTUNITY',
  CONNECT_SOCIAL_ACCOUNT = 'CONNECT_SOCIAL_ACCOUNT',
  CREATE_SOCIAL_POST = 'CREATE_SOCIAL_POST',
  SETUP_FACEBOOK_INTEGRATION = 'SETUP_INTEGRATION',
  SETUP_FORM_FIELD_MAPPING = 'SETUP_FORM_FIELD_MAPPING',
  SETUP_SMS_TEMPLATE = 'SETUP_SMS_TEMPLATE',
  SETUP_SMS_CAMPAIGN = 'SETUP_SMS_CAMPAIGN',
  SYNC_GMAIL_OUTLOOK = 'SYNC_GMAIL_OUTLOOK',
  SETUP_CHAT_WIDGET = 'SETUP_CHAT_WIDGET',
  SETUP_FACEBOOK_MESSENGER = 'SETUP_FACEBOOK_MESSENGER',
  INSTALL_MOBILE_APP = 'INSTALL_MOBILE_APP',
  CREATE_WEBSITE = 'CREATE_WEBSITE',
  CREATE_WEBSITE_STEP = 'CREATE_WEBSITE_STEP',
  PUBLISH_WEBSITE = 'PUBLISH_WEBSITE',
  MIGRATE_WORDPRESS = 'MIGRATE_WORDPRESS',
  CONNECT_STRIPE = 'CONNECT_STRIPE',
  CREATE_PRODUCT = 'CREATE_PRODUCT',
  CREATE_COUPON = 'CREATE_COUPON',
  CREATE_PAYMENT_LINK = 'CREATE_PAYMENT_LINK',
  CREATE_INVOICE = 'CREATE_INVOICE',
  CREATE_COURSE = 'CREATE_COURSE',
}

export enum LaunchpadLocationSegmentStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export enum SegmentTypesEnum {
  AGENCY = 'agency',
  LOCATION = 'location',
}

export interface IStepInfo {
  id: string
  eventName: IEventName
  status: LaunchpadLocationSegmentStatus
  notified: boolean
  checkStatus: boolean
  productId: string
}

export interface ILaunchpadProgress {
  stepInfo: IStepInfo[]
  refId: string
  type: SegmentTypesEnum
}

let launchpadFirestore: Firestore
const getLaunchpadFirestore = () => {
  if (launchpadFirestore) {
    return launchpadFirestore
  }
  const database =
    process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'beta'
      ? 'leadgen-onboarding-production'
      : 'leadgen-onboarding-staging'
  launchpadFirestore = initializeFirestore(getApp(), {}, database)
  return launchpadFirestore
}

export default class LaunchpadProgress {
  public static collectionRef() {
    return collection(getLaunchpadFirestore(), 'launchpad_progress')
  }

  private _data: DocumentData
  private _id: string
  private _ref: DocumentReference
  private _snapshot: DocumentSnapshot

  constructor(snapshot?: DocumentSnapshot | QueryDocumentSnapshot | { [key: string]: any }) {
    if (snapshot instanceof DocumentSnapshot || snapshot instanceof QueryDocumentSnapshot) {
      this._id = snapshot.id
      this._data = snapshot.data() || {}
      this._ref = snapshot.ref
      this._snapshot = snapshot
    } else if (snapshot) {
      this._id = snapshot.id
      this._data = snapshot
      this._ref = doc(LaunchpadProgress.collectionRef(), snapshot.id)
    } else {
      this._ref = doc(LaunchpadProgress.collectionRef())
      this._id = this._ref.id
      this._data = {}
    }
  }

  public static getById(id): Promise<LaunchpadProgress> {
    return new Promise(async (resolve, reject) => {
      const docRef = doc(getLaunchpadFirestore(), 'launchpad_progress', id)
      const snapshot = await getDoc(docRef)

      if (snapshot.exists()) {
        resolve(new LaunchpadProgress(snapshot))
      } else {
        reject(new Error(`Document with ID ${id} does not exist`))
      }
    })
  }

  public static getByRefId(refId: string): Promise<LaunchpadProgress> {
    return new Promise(async (resolve, reject) => {
      try {
        const colRef = LaunchpadProgress.collectionRef()
        const q = query(colRef, where('refId', '==', refId), where('deleted', '==', false))

        const snapshot = await getDocs(q)

        if (!snapshot.empty) {
          resolve(new LaunchpadProgress(snapshot.docs[0] as QueryDocumentSnapshot<DocumentData>))
        } else {
          reject(new Error(`No document found with refId: ${refId}`))
        }
      } catch (err) {
        reject(err)
      }
    })
  }

  public static fetchByRefId(refId: string) {
    const colRef = LaunchpadProgress.collectionRef()
    return query(colRef, where('refId', '==', refId), where('deleted', '==', false), limit(1))
  }

  get ref(): DocumentReference {
    return this._ref
  }

  get snapshot() {
    return this._snapshot
  }

  get data(): DocumentData {
    return this._data
  }

  get stepInfo(): IStepInfo[] {
    return this._data.stepInfo
  }

  set stepInfo(stepInfo: IStepInfo[]) {
    this._data.stepInfo = stepInfo
  }

  get refId(): string {
    return this._data.refId
  }

  set refId(refId: string) {
    this._data.refId = refId
  }

  get type(): SegmentTypesEnum {
    return this._data.type
  }

  set type(type: SegmentTypesEnum) {
    this._data.type = type
  }
}
